import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Search, Package, Truck, CheckCircle2, Clock, MapPin } from "lucide-react";

const statusIcon = (st) => {
  const s = (st || "").toLowerCase();
  if (s.includes("deliver")) return CheckCircle2;
  if (s.includes("ship") || s.includes("transit") || s.includes("on") || s.includes("courier")) return Truck;
  if (s.includes("pick")) return Package;
  return Clock;
};

const Tracking = () => {
  const { lang } = useApp();
  const [params] = useSearchParams();
  const [orderNum, setOrderNum] = useState(params.get("order") || "");
  const [waybill, setWaybill] = useState("");
  const [courierCode, setCourierCode] = useState("jne");
  const [mode, setMode] = useState("order");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setErr(""); setData(null); setLoading(true);
    try {
      let res;
      if (mode === "order") {
        if (!orderNum) throw new Error(lang === "id" ? "Masukkan nomor pesanan" : "Enter order number");
        const { data } = await api.get(`/shipping/track/order/${encodeURIComponent(orderNum.trim())}`);
        res = data;
      } else {
        if (!waybill) throw new Error(lang === "id" ? "Masukkan nomor resi" : "Enter waybill");
        const { data } = await api.get(`/shipping/track/waybill/${encodeURIComponent(waybill.trim())}/courier/${courierCode}`);
        res = data;
      }
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Not found");
    } finally { setLoading(false); }
  };

  const history = data?.tracking_history || data?.history || [];

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <div className="text-center mb-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">✦ Live Tracking ✦</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-holy-red mb-2">
          {lang === "id" ? "Lacak Pesanan" : "Track Your Order"}
        </h1>
        <p className="text-muted-foreground">
          {lang === "id" ? "Pantau status paket Anda secara real-time" : "Track your package status in real-time"}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-border/60 p-6 mb-6">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode("order")} data-testid="track-mode-order" className={`flex-1 px-4 py-2 rounded-full text-sm transition-all ${mode === "order" ? "bg-holy-red text-ivory" : "bg-cream text-foreground"}`}>
            {lang === "id" ? "Nomor Pesanan" : "Order Number"}
          </button>
          <button onClick={() => setMode("waybill")} data-testid="track-mode-waybill" className={`flex-1 px-4 py-2 rounded-full text-sm transition-all ${mode === "waybill" ? "bg-holy-red text-ivory" : "bg-cream text-foreground"}`}>
            {lang === "id" ? "Resi Kurir" : "Courier Waybill"}
          </button>
        </div>

        {mode === "order" ? (
          <input
            data-testid="track-order-input"
            placeholder={lang === "id" ? "Contoh: HF-260221-A1B2C3" : "e.g. HF-260221-A1B2C3"}
            value={orderNum}
            onChange={(e) => setOrderNum(e.target.value)}
            className="input-base"
          />
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            <input
              data-testid="track-waybill-input"
              placeholder={lang === "id" ? "Nomor resi" : "Waybill number"}
              value={waybill}
              onChange={(e) => setWaybill(e.target.value)}
              className="input-base"
            />
            <select value={courierCode} onChange={(e) => setCourierCode(e.target.value)} className="input-base">
              <option value="jne">JNE</option>
              <option value="jnt">J&T Express</option>
              <option value="pos">Pos Indonesia</option>
              <option value="sicepat">SiCepat</option>
              <option value="anteraja">AnterAja</option>
              <option value="gojek">Gojek</option>
              <option value="grab">Grab</option>
              <option value="ninja">Ninja Xpress</option>
              <option value="lion">Lion Parcel</option>
              <option value="dhl">DHL</option>
              <option value="fedex">FedEx</option>
            </select>
          </div>
        )}

        <button data-testid="track-search-btn" onClick={search} disabled={loading} className="mt-4 inline-flex items-center gap-2 bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-all disabled:opacity-50">
          <Search className="w-4 h-4" /> {loading ? "..." : (lang === "id" ? "Lacak" : "Track")}
        </button>
      </div>

      {err && <div className="bg-holy-red/5 border border-holy-red/20 text-holy-red rounded-xl p-4 text-sm" data-testid="track-error">{err}</div>}

      {data && (
        <div className="bg-white rounded-2xl border border-border/60 p-6" data-testid="track-result">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</div>
              <div className="font-serif text-2xl text-holy-red capitalize">{data.status || (history[0]?.status) || "—"}</div>
              {data.order_number && <div className="text-xs text-muted-foreground mt-1">Order #{data.order_number}</div>}
            </div>
            {data.shipping_courier && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Courier</div>
                <div className="font-medium">{data.shipping_courier} · {data.shipping_service}</div>
                {data.waybill_id && <div className="text-xs font-mono text-muted-foreground">{data.waybill_id}</div>}
              </div>
            )}
          </div>

          {history.length > 0 ? (
            <div className="relative pl-6 border-l-2 border-cream space-y-5">
              {history.map((h, i) => {
                const Icon = statusIcon(h.status);
                return (
                  <div key={i} className="relative">
                    <span className={`absolute -left-[33px] top-0 w-6 h-6 rounded-full flex items-center justify-center ${i === 0 ? "bg-holy-red text-ivory" : "bg-cream text-foreground"}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="text-sm font-medium">{h.note || h.status}</div>
                    <div className="text-xs text-muted-foreground">{h.updated_at || h.timestamp}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground bg-cream rounded-xl p-4 text-center">
              {lang === "id"
                ? "Belum ada update tracking. Paket Anda akan segera diproses."
                : "No tracking updates yet. Your package will be processed soon."}
            </div>
          )}
        </div>
      )}

      <style>{`.input-base{background:#fff;border:1px solid hsl(var(--border));padding:.75rem 1rem;border-radius:.75rem;font-size:.875rem;outline:none;width:100%}.input-base:focus{border-color:#9B1C26;box-shadow:0 0 0 3px rgba(155,28,38,.1)}`}</style>
    </div>
  );
};

export default Tracking;
